import {highlightExistingAction} from '../../core/actionHighlighter';

// Canvas names these header controls by their action key: the icon carries
// aria-label="download", and the readable text lives in a Tooltip wrapper
// that only renders on hover, so it is never on the element. The readable
// text is kept second in case the markup gains a real label later.
const ACTION_LABELS = {
	clone: ['groupClone', 'Clone Site'],
	import_configuration: ['import', 'Import Site Configuration'],
	export_configuration: ['export', 'Download site configuration (txt)'],
	download_report: ['download', 'Download Site Dashboard Report (PDF)'],
};

/**
 * Build Canvas router state for a Site destination.
 * @param {object} command navigation command
 * @return {object|null} router state
 */
export function buildSiteNavigationState(command) {
	if (!command.params?.site_name) return null;
	const siteName = command.params.site_name;
	return {
		type: 'group',
		group: siteName,
		res: siteName.replace(/\s*\|\s*/g, ' and '),
		site_id: command.params.site_id || null,
	};
}

/**
 * Compare two Site names the way Canvas writes them.
 *
 * The same Site is called two things: its own name in the header, and the
 * trail "Child | Parent" it is listed under. A lookup hands back whichever
 * its endpoint carries, so the arrival is checked against both, each read
 * down to the Site it actually names.
 *
 * @param {object} context registered page context
 * @param {string|undefined} requested name the command asked for
 * @return {boolean} whether the Site on screen is the one asked for
 */
function matchesSiteName(context, requested) {
	if (!requested) return true;
	const wanted = leafOf(requested);
	return [context.site_name, context.site_path].some(
		(known) => known && leafOf(known) === wanted,
	);
}

/**
 * The Site a trail names, out of the Sites containing it.
 *
 * Canvas writes a trail innermost first - "test | pankajCHeck56" is the
 * Site "test" inside "pankajCHeck56" - so the Site's own name is the first
 * segment. Read from the other end it was the container's name, and a
 * nested Site answered to whatever it sat in: standing inside "test", a
 * request for "pankajCHeck56" was taken as already arrived, and the agent
 * went on to open a section on the child's page believing it was the
 * parent's. Only the site_id check kept that from being acted on, and a
 * lookup that hands back no id has nothing else to catch it.
 *
 * @param {string} name a Site name, or the trail it is listed under
 * @return {string} the name of the Site itself
 */
function leafOf(name) {
	return String(name).split('|')[0].trim();
}

/**
 * Compare a Site identifier against the ones the page publishes.
 *
 * A nested Site is keyed by nested_site_id and a top-level one by site_id,
 * and which of them a lookup returned is not something the arrival check
 * can know - so any published identifier answering to it counts.
 *
 * @param {object} context registered page context
 * @param {string|undefined} requested identifier the command asked for
 * @return {boolean} whether the Site on screen is the one asked for
 */
function matchesSiteId(context, requested) {
	if (!requested) return true;
	return [context.site_id, context.nested_site_id].some(
		(known) => known != null && String(known) === String(requested),
	);
}

/**
 * Verify that the requested Site destination mounted.
 * @param {object} command navigation command
 * @param {object} context registered page context
 * @return {boolean} readiness status
 */
export function isSiteNavigationReady(command, context) {
	const requestedSite = command.params?.site_name;
	const requestedSiteId = command.params?.site_id;
	const ready = Boolean(context.site_id) &&
		matchesSiteId(context, requestedSiteId) &&
		matchesSiteName(context, requestedSite);
	if (!ready) return false;
	const actionTarget = command.params?.action_target;
	if (!actionTarget || !ACTION_LABELS[actionTarget]) return true;
	const shown = highlightExistingAction(ACTION_LABELS[actionTarget], [], {
		activate: command.params?.action_activation === 'auto',
		commandId: command.command_id,
	});
	// The Site arrived; only the control the request needs is missing. Saying
	// "navigation failed" here sent the user back to a page they were already
	// looking at.
	return shown ? true : {ready: false, reason: 'action_control_unavailable'};
}
